/**
 * AppDefaults — web-side mirror of iOS's AppDefaults.swift. Holds
 * the cross-platform defaults the wasm shim (GetDefaults →
 * internal/core/defaults.go) hands to clients on boot.
 *
 * Reads are synchronous so call sites stay simple (default-arg
 * shape, saga fallbacks, etc.). bootstrap() runs once at app
 * launch from `src/index.tsx`, before the React tree mounts;
 * pre-bootstrap access sees the literal fallbacks below, which
 * intentionally mirror Go's compile-time constants so a missing
 * /core.wasm asset degrades to "today's snapshot baked into the
 * binary" rather than crashing.
 *
 * Server-side Go callers read the same numbers from
 * `core.VanishDefaultTime` / `core.DayStartHourFallback` /
 * `core.DefaultHourFallback`; the parity test in `internal/api`
 * gates the two from drifting.
 */

import { Tier2 } from './Tier2';

interface DefaultsSnapshot {
  vanishDefaultTime: string;
  dayStartHourFallback: number;
  defaultHourFallback: number;
}

const snapshot: DefaultsSnapshot = {
  vanishDefaultTime: '03:00',
  dayStartHourFallback: 6,
  defaultHourFallback: 10,
};

export const AppDefaults = {
  get vanishDefaultTime(): string {
    return snapshot.vanishDefaultTime;
  },
  get dayStartHourFallback(): number {
    return snapshot.dayStartHourFallback;
  },
  get defaultHourFallback(): number {
    return snapshot.defaultHourFallback;
  },
};

/**
 * bootstrap loads /core.wasm and refreshes AppDefaults from its
 * GetDefaults export. Idempotent. Errors are logged but non-fatal:
 * the fallback literals above match what the wasm shim would have
 * returned anyway, so a load failure leaves the app correct (just
 * frozen at the build-time values until the next deploy).
 */
export async function bootstrapAppDefaults(url = '/core.wasm'): Promise<void> {
  try {
    const tier2 = await Tier2.loadFromUrl(url);
    const d = tier2.getDefaults();
    snapshot.vanishDefaultTime = d.vanish_default_time;
    snapshot.dayStartHourFallback = d.day_start_hour_fallback;
    snapshot.defaultHourFallback = d.default_hour_fallback;
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[wasm] AppDefaults bootstrapped', snapshot);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[wasm] AppDefaults bootstrap failed; using fallbacks', e);
  }
}

/**
 * resetAppDefaultsForTests — test-only escape hatch. Real code
 * never needs this; it exists so a unit test can verify the
 * fallback values without observing residue from a prior test
 * that called bootstrap().
 */
export function resetAppDefaultsForTests(): void {
  snapshot.vanishDefaultTime = '03:00';
  snapshot.dayStartHourFallback = 6;
  snapshot.defaultHourFallback = 10;
}
